import React from 'react';
import { SvgIconProps } from '@mui/material';
import WebStoriesIcon from '@mui/icons-material/WebStories';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AssignmentIcon from '@mui/icons-material/Assignment';
import StoreIcon from '@mui/icons-material/Store';
import InventoryIcon from '@mui/icons-material/Inventory';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ImageIcon from '@mui/icons-material/Image';
import PersonIcon from '@mui/icons-material/Person';
import StyleIcon from '@mui/icons-material/Style';
import BugReportIcon from '@mui/icons-material/BugReport';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import PrintIcon from '@mui/icons-material/Print';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BlurOnIcon from '@mui/icons-material/BlurOn';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import { Requirement } from '../../auth/permissions';
import {
    MODERATOR, ORDERS_R, STORIES_R, STORY_VIEWER, MOCKUP_HISTORY_R, MOCKUP_R, AAV_PRODUCTION_R, AAV_SHOPIFY_R,
} from '../../auth/requirements';
import { PRINTFULL_ID, FREEPRINTS_ID, INK_ID, DRAWPILLOWS_ID, TEST_ID, PRINTIFY_ID, PRINTIFY_TEST_ID } from '../../constants';

export type NavSectionId = 'api' | 'stories' | 'mockups' | 'production';

export interface NavItem {
    title: string;
    /** Route path without the leading slash, e.g. `stories/stats`. */
    link: string;
    Icon: React.ComponentType<SvgIconProps>;
    requires: Requirement;
    /** Rendered inside the "Archived companies" sub-accordion and linked with `?archived=true`. */
    archived?: boolean;
}

export interface NavSection {
    id: NavSectionId;
    title: string;
    items: NavItem[];
}

/** The whole left menu; an item is shown when the user satisfies `requires`, a section when it has a visible item. */
export const NAV_SECTIONS: NavSection[] = [
    {
        id: 'api',
        title: 'API Integration',
        items: [
            { title: 'Printful Orders', link: `${PRINTFULL_ID}/orders`, Icon: ListAltIcon, requires: ORDERS_R },
            { title: 'Freeprints Orders', link: `${FREEPRINTS_ID}/orders`, Icon: ListAltIcon, requires: ORDERS_R, archived: true },
            { title: 'DrawPillows Orders', link: `${DRAWPILLOWS_ID}/orders`, Icon: ListAltIcon, requires: ORDERS_R },
            { title: 'Printify Orders', link: `${PRINTIFY_ID}/orders`, Icon: ListAltIcon, requires: ORDERS_R },
            { title: 'PrintifyTest Orders', link: `${PRINTIFY_TEST_ID}/orders`, Icon: ListAltIcon, requires: ORDERS_R, archived: true },
            { title: 'INK Orders', link: `${INK_ID}/orders`, Icon: ListAltIcon, requires: ORDERS_R, archived: true },
            { title: 'Test Orders', link: `${TEST_ID}/orders`, Icon: ListAltIcon, requires: ORDERS_R, archived: true },
            { title: 'Products', link: 'products', Icon: InventoryIcon, requires: MODERATOR },
            { title: 'Companies', link: 'companies', Icon: StoreIcon, requires: MODERATOR },
            { title: 'Shipments', link: 'shipment-issues', Icon: LocalShippingIcon, requires: ORDERS_R },
            { title: 'Orders By Company', link: 'orders-tracking', Icon: BugReportIcon, requires: ORDERS_R },
        ],
    },
    {
        id: 'stories',
        title: 'Customer Stories',
        items: [
            { title: 'Authors', link: 'authors', Icon: PeopleAltIcon, requires: STORIES_R },
            { title: 'Stories', link: 'stories', Icon: WebStoriesIcon, requires: STORIES_R },
            { title: 'Stories Tags', link: 's-tags', Icon: StyleIcon, requires: STORIES_R },
            { title: 'Tags & Categories', link: 'story-tags', Icon: LocalOfferIcon, requires: STORIES_R },
            { title: 'Stories Stats', link: 'stories/stats', Icon: ShowChartIcon, requires: STORIES_R },
            { title: 'Story Viewer', link: 'story-viewer', Icon: VideoLibraryIcon, requires: STORY_VIEWER },
        ],
    },
    {
        id: 'mockups',
        title: 'Mockups',
        items: [
            { title: 'Mockups Preview', link: 'mockups-image', Icon: ImageIcon, requires: MOCKUP_HISTORY_R },
            { title: 'Coloring Preview', link: 'mockups-coloring', Icon: ImageIcon, requires: MOCKUP_R },
            { title: 'Blanket Preview', link: 'mockups-towel', Icon: ImageIcon, requires: MOCKUP_R },
            { title: 'Mockups Stats', link: 'mockups-stats', Icon: ShowChartIcon, requires: MOCKUP_HISTORY_R },
            { title: 'Customers', link: 'mockups-customers', Icon: PersonIcon, requires: MOCKUP_R },
            { title: 'Mockups A/B Testing', link: 'mockups-compare-stats', Icon: SignalCellularAltIcon, requires: MOCKUP_HISTORY_R },
            { title: 'RemoveBG Logs', link: 'removebg-logs', Icon: BugReportIcon, requires: MOCKUP_R },
            { title: 'Mockup Logs', link: 'mockup-logs', Icon: TroubleshootIcon, requires: MOCKUP_R },
            { title: 'Generation Logs', link: 'mockup-generation-logs', Icon: TroubleshootIcon, requires: MOCKUP_R },
        ],
    },
    {
        id: 'production',
        title: 'Production',
        items: [
            { title: 'Production Time', link: 'track-order', Icon: HourglassTopIcon, requires: AAV_PRODUCTION_R },
            { title: 'Pillow Search', link: 'pillow-segmentation', Icon: BlurOnIcon, requires: AAV_PRODUCTION_R },
            { title: 'Form Submissions', link: 'customer-form-submissions', Icon: AssignmentIcon, requires: AAV_SHOPIFY_R },
            { title: 'Print File Review', link: 'print-file-review', Icon: FactCheckIcon, requires: AAV_PRODUCTION_R },
            { title: 'Print File Jobs', link: 'print-file-jobs', Icon: PrintIcon, requires: AAV_PRODUCTION_R },
        ],
    },
];

/** "Printful Orders" menu title → "Printful" for the company id of an orders page; unknown ids give `fallback`. */
export const companyNameFor = (id: string | undefined, fallback = 'Company'): string => {
    const item = NAV_SECTIONS.flatMap((section) => section.items).find((entry) => entry.link === `${id}/orders`);
    return item ? item.title.replace(/ Orders$/, '') : fallback;
};

/** Pages without a menu entry still expand the section they belong to. */
export const HIDDEN_ROUTE_SECTIONS: { prefix: string; section: NavSectionId }[] = [
    { prefix: 'orders-analytics', section: 'api' },
    { prefix: 'aav-orders', section: 'production' },
    { prefix: 'health-check', section: 'mockups' },
    { prefix: 'session-conversion', section: 'mockups' },
    { prefix: '3d-analytics', section: 'mockups' },
    { prefix: '3d-mockups', section: 'mockups' },
    { prefix: 'model-front-logs', section: 'production' },
];
